import { useEffect, useRef } from 'react';
import type { FieldValues, Path, PathValue, UseFormSetValue, UseFormWatch } from 'react-hook-form';

interface UseResetOnFieldChangeOptions<T extends FieldValues> {
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
  whenChanged: Path<T>;
  reset: readonly Path<T>[];
}

export function useResetOnFieldChange<T extends FieldValues>({
  watch,
  setValue,
  whenChanged,
  reset,
}: UseResetOnFieldChangeOptions<T>): void {
  const resetFieldsRef = useRef(reset);

  useEffect(() => {
    resetFieldsRef.current = reset;
  }, [reset]);

  useEffect(() => {
    const subscription = watch((_value, { name, type }) => {
      if (name !== whenChanged || type !== 'change') return;
      for (const field of resetFieldsRef.current) {
        setValue(field, undefined as PathValue<T, typeof field>, { shouldDirty: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, whenChanged]);
}
